<template>
    <nav
        class="relative flex-1 overflow-y-auto p-4 scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <ul class="m-0 flex list-none flex-col gap-3 p-0">
            <li v-for="item in sidebarNavigationItems" :key="item.label">
                <template v-if="item.hasSubRoutes">
                    <div
                        class="relative rounded-(--border-radius-md) shadow-(--sidebar-nav-item-shadow) after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:opacity-0 after:shadow-(--sidebar-nav-item-hover-shadow) after:transition-opacity after:duration-150 after:ease-out after:content-[''] hover:after:opacity-100 focus-visible:shadow-(--sidebar-nav-item-focus-shadow)"
                        tabindex="-1">
                        <div class="overflow-hidden rounded-[inherit] bg-(image:--button-light-surface) outline-none">
                            <SidebarNavItem
                                :label="item.label"
                                :to="item.hasRoute ? item.route : undefined"
                                :as="item.hasRoute ? 'span' : 'button'"
                                :active="isGroupActive(item)"
                                :variant="SidebarNavItemVariants.Group"
                                @click="onGroupClick(item)">
                                <template #icon>
                                    <Motion
                                        :while-hover="SidebarNavigationMotion.GroupIconHover"
                                        :transition="SidebarNavigationMotion.IconTransition"
                                        class="origin-[center_center] will-change-transform">
                                        <SidebarNavIcon :name="item.icon" :active="isGroupActive(item)" />
                                    </Motion>
                                </template>

                                <Motion
                                    as="span"
                                    :animate="groupState(item)"
                                    :initial="false"
                                    :transition="SidebarNavigationMotion.ChevronTransition"
                                    :variants="SIDEBAR_CHEVRON_VARIANTS"
                                    class="flex h-6 w-6 shrink-0 items-center justify-center text-(--text-muted) group-hover:text-(--text-light)">
                                    <Icon name="br-icon:chevron-up" size="9" />
                                </Motion>
                            </SidebarNavItem>

                            <Motion
                                :animate="groupState(item)"
                                :initial="false"
                                :transition="DRAWER_TRANSITION"
                                :variants="DRAWER_HEIGHT_VARIANTS"
                                class="overflow-hidden">
                                <div
                                    class="relative m-[0_0.375rem_0.375rem] flex flex-col gap-1 rounded-[0.625rem] bg-[rgba(0,0,0,0.025)] p-[0.375rem_0.5rem_0.5rem_0.5rem] shadow-[inset_0.125rem_0.125rem_0.25rem_rgba(5,5,5,0.06),inset_-0.063rem_-0.063rem_0.188rem_rgba(255,255,255,0.3)]">
                                    <NuxtLink
                                        v-for="(sub, index) in item.subRoutes"
                                        :key="sub.label"
                                        v-slot="{ isActive }"
                                        :to="sub.route">
                                        <Motion
                                            as="button"
                                            :animate="groupState(item)"
                                            :custom="index"
                                            :initial="false"
                                            :transition="SidebarNavigationMotion.ItemCollapseTransition"
                                            :variants="SidebarItemRevealVariants"
                                            :class="[
                                                'group relative flex h-9.5 w-full cursor-pointer items-center gap-2 rounded-[0.625rem] border-none bg-transparent px-2 font-inherit text-[0.8rem] font-medium text-(--text-button-light) outline-none hover:bg-(--border-glass) hover:text-(--text-button-light-strong) focus-visible:text-(--text-button-light-strong)',
                                                isActive &&
                                                    'bg-(--sidebar-active-background)! font-semibold text-(--input-neumorphic-text) shadow-[inset_0.188rem_0_0_var(--text-button-light-strong)]'
                                            ]">
                                            <Motion
                                                :while-hover="SidebarNavigationMotion.SubitemIconHover"
                                                :transition="SidebarNavigationMotion.IconTransition">
                                                <SidebarNavIcon
                                                    :name="sub.icon"
                                                    :active="isActive"
                                                    :variant="SidebarNavIconVariants.Sub" />
                                            </Motion>
                                            <span
                                                class="flex-1 overflow-hidden text-left text-ellipsis whitespace-nowrap">
                                                {{ sub.label }}
                                            </span>
                                        </Motion>
                                    </NuxtLink>
                                </div>
                            </Motion>
                        </div>
                    </div>
                </template>

                <template v-else-if="item.hasRoute">
                    <NuxtLink v-slot="{ isActive }" :to="item.route">
                        <SidebarNavItem :label="item.label" :active="isActive">
                            <template #icon>
                                <SidebarNavIcon :name="item.icon" :active="isActive" />
                            </template>
                        </SidebarNavItem>
                    </NuxtLink>
                </template>

                <template v-else>
                    <SidebarNavItem :label="item.label" :variant="SidebarNavItemVariants.Disabled">
                        <template #icon>
                            <SidebarNavIcon :name="item.icon" />
                        </template>
                    </SidebarNavItem>
                </template>
            </li>
        </ul>
    </nav>
</template>

<script lang="ts" setup>
    import { NuxtLink } from '#components';
    import type { Variant } from 'motion-v';

    import { UIState, type UIStateType } from '@constants/ui/ui-state';
    import {
        DRAWER_HEIGHT_VARIANTS,
        SIDEBAR_CHEVRON_VARIANTS,
        DRAWER_TRANSITION,
        SidebarNavigationMotion
    } from '@constants/motion';
    import { Opacity } from '@constants/motion/defaults';
    import { SidebarNavIconVariants } from '@/assets/css/themes/custom-components/constants/sidebar-nav-icon';
    import { SidebarNavItemVariants } from '@/assets/css/themes/custom-components/constants/sidebar-nav-item';

    import { useSidebarNavigation } from '@/composables/useSidebarNavigation';
    import { getSidebarItemMotionDelay } from '@/utils/motion';
    import type { NavItem } from '@/types/navigation/sidebar-navigation';

    const SidebarItemRevealVariants = Object.freeze({
        [UIState.Collapsed]: { opacity: Opacity.Hidden },
        [UIState.Expanded]: (custom: unknown) => ({
            opacity: Opacity.Visible,
            transition: {
                ...SidebarNavigationMotion.ChevronTransition,
                delay: getSidebarItemMotionDelay(custom)
            }
        })
    } satisfies Record<UIStateType, Variant>);

    const route = useRoute();
    const { sidebarNavigationItems, matchedGroupLabels } = useSidebarNavigation();

    const manualOpenMap = reactive<Record<string, boolean | undefined>>({});

    const isOpen = (item: NavItem) => manualOpenMap[item.label] ?? matchedGroupLabels.value.has(item.label);

    const groupState = (item: NavItem) => (isOpen(item) ? UIState.Expanded : UIState.Collapsed);

    const isGroupActive = (item: NavItem) =>
        item.hasRoute && item.route ? route.path === item.route || route.path.startsWith(item.route + '/') : false;

    watch(
        () => route.path,
        () => {
            for (const key of Object.keys(manualOpenMap)) {
                delete manualOpenMap[key];
            }
        }
    );

    function toggleGroup(item: NavItem) {
        manualOpenMap[item.label] = !isOpen(item);
    }

    async function onGroupClick(item: NavItem) {
        toggleGroup(item);

        if (item.route) {
            await navigateTo(item.route);
        }
    }
</script>
